import { z } from "zod";

import {
  EventIdPublic,
  MessageId,
  MessageThreadId,
} from "./branded-id.schemas";
import type { EventBrandingField } from "./event-branding-upload.schemas.js";

export const StorageFileType = {
  EVENT_DOCUMENT: "event-document",
  EVENT_COVER_IMAGE: "event-cover-image",
  EVENT_BRANDING_LOGO: "event-branding-logo",
  EVENT_BRANDING_KEY_VISUAL: "event-branding-key-visual",
  AVATAR_IMAGE: "avatar-image",
  ORGANIZATION_LOGO: "organization-logo",
  IMPORT_CSV: "import-csv",
  MESSAGE_AUDIO: "message-audio",
} as const;

export type StorageFileTypeType =
  (typeof StorageFileType)[keyof typeof StorageFileType];

export const StorageFileTypeConfigSchema = z.object({
  maxSizeBytes: z.number(),
  allowedMimeTypes: z.array(z.string()),
});

export type StorageFileTypeConfig = z.infer<typeof StorageFileTypeConfigSchema>;

export const STORAGE_FILE_TYPE_CONFIGS: Record<
  StorageFileTypeType,
  StorageFileTypeConfig
> = {
  [StorageFileType.EVENT_DOCUMENT]: {
    maxSizeBytes: 10 * 1024 * 1024, // 10 MB
    allowedMimeTypes: ["application/pdf"],
  },
  [StorageFileType.EVENT_COVER_IMAGE]: {
    maxSizeBytes: 5 * 1024 * 1024, // 5 MB
    allowedMimeTypes: ["image/jpeg", "image/png", "image/webp"],
  },
  [StorageFileType.EVENT_BRANDING_LOGO]: {
    maxSizeBytes: 2 * 1024 * 1024, // 2 MB
    allowedMimeTypes: ["image/jpeg", "image/png", "image/webp"],
  },
  [StorageFileType.EVENT_BRANDING_KEY_VISUAL]: {
    maxSizeBytes: 5 * 1024 * 1024, // 5 MB
    allowedMimeTypes: ["image/jpeg", "image/png", "image/webp"],
  },
  [StorageFileType.AVATAR_IMAGE]: {
    maxSizeBytes: 20 * 1024 * 1024, // 20 MB
    allowedMimeTypes: ["image/jpeg", "image/png", "image/webp"],
  },
  [StorageFileType.ORGANIZATION_LOGO]: {
    maxSizeBytes: 2 * 1024 * 1024, // 2 MB
    allowedMimeTypes: [
      "image/jpeg",
      "image/png",
      "image/webp",
      "image/svg+xml",
    ],
  },
  [StorageFileType.IMPORT_CSV]: {
    maxSizeBytes: 5 * 1024 * 1024, // 5 MB
    allowedMimeTypes: ["text/csv", "application/vnd.ms-excel"],
  },
  [StorageFileType.MESSAGE_AUDIO]: {
    maxSizeBytes: 16 * 1024 * 1024, // 16 MB — fits WhatsApp voice-note size cap with headroom
    allowedMimeTypes: ["audio/ogg", "audio/mpeg", "audio/mp4"],
  },
};

export const ENRICHMENT_PHOTO_URL_SCHEME = "enrichment-photo://";

export function resolveEnrichmentPhotoStoragePath(url: string): string | null {
  if (!url.startsWith(ENRICHMENT_PHOTO_URL_SCHEME)) {
    return null;
  }
  const path = url.slice(ENRICHMENT_PHOTO_URL_SCHEME.length);
  if (path.length === 0) {
    return null;
  }
  return `enrichment/user-photos/${path}`;
}

export const MESSAGE_AUDIO_URL_SCHEME = "message-audio://";

export function buildMessageAudioUrl(
  messageThreadId: MessageThreadId,
  messageId: MessageId,
): string {
  // Serialization boundary: branded ids → portable storage URL string. The
  // rule's path check doesn't recognise shared/ url builders (peers like
  // subdomain-url.utils.ts unwrap here too).
  // eslint-disable-next-line planta-v2/brand-unwrap-only-at-boundaries -- storage URL serialization
  return `${MESSAGE_AUDIO_URL_SCHEME}${MessageThreadId.unwrap(messageThreadId)}/${MessageId.unwrap(messageId)}`;
}

const MESSAGE_AUDIO_STORAGE_PREFIX = "communications/audio";

export function buildMessageAudioStoragePath(
  messageThreadId: MessageThreadId,
  messageId: MessageId,
): string {
  const urlSuffix = buildMessageAudioUrl(messageThreadId, messageId).slice(
    MESSAGE_AUDIO_URL_SCHEME.length,
  );
  return `${MESSAGE_AUDIO_STORAGE_PREFIX}/${urlSuffix}`;
}

export function resolveMessageAudioStoragePath(url: string): string | null {
  if (!url.startsWith(MESSAGE_AUDIO_URL_SCHEME)) {
    return null;
  }
  const suffix = url.slice(MESSAGE_AUDIO_URL_SCHEME.length);
  if (suffix.length === 0 || suffix.includes("..") || suffix.startsWith("/")) {
    return null;
  }
  return `${MESSAGE_AUDIO_STORAGE_PREFIX}/${suffix}`;
}

export const PARTICIPANT_PHOTO_URL_SCHEME = "evt-participant-photo://";

export function extractPhotoKeyFromPhotoUrl(
  url: string | null,
): string | undefined {
  if (url === null || !url.startsWith(PARTICIPANT_PHOTO_URL_SCHEME)) {
    return undefined;
  }
  const path = url.slice(PARTICIPANT_PHOTO_URL_SCHEME.length);
  const segments = path.split("/").filter(Boolean);

  if (segments.length === 2) {
    return segments[1];
  }
  return undefined;
}

export const EPHEMERAL_FILE_URL_SCHEME = "ephemeral-file://";

const EPHEMERAL_FILE_STORAGE_PREFIX = "ephemeral";

/**
 * Files under this prefix are deleted by the bucket's lifecycle rule, not by
 * any code path — nothing here writes an expiry or sweeps the prefix.
 */
export function buildEphemeralFileStoragePath(fileKey: string): string {
  return `${EPHEMERAL_FILE_STORAGE_PREFIX}/${fileKey}`;
}

export function buildEphemeralFileUrl(fileKey: string): string {
  return `${EPHEMERAL_FILE_URL_SCHEME}${fileKey}`;
}

export function resolveEphemeralFileStoragePath(url: string): string | null {
  if (!url.startsWith(EPHEMERAL_FILE_URL_SCHEME)) {
    return null;
  }
  const fileKey = url.slice(EPHEMERAL_FILE_URL_SCHEME.length);
  if (fileKey.length === 0) {
    return null;
  }
  return buildEphemeralFileStoragePath(fileKey);
}

export type PendingEventBrandingScope =
  | { readonly type: "create" }
  | { readonly type: "event"; readonly eventIdPublic: EventIdPublic };

export function buildPendingEventBrandingStoragePath(
  scope: PendingEventBrandingScope,
  field: string,
  assetKey: string,
): string {
  const scopePath =
    scope.type === "create"
      ? "create"
      : // eslint-disable-next-line planta-v2/brand-unwrap-only-at-boundaries -- storage path serialization
        `events/${EventIdPublic.unwrap(scope.eventIdPublic)}`;
  return buildEphemeralFileStoragePath(
    `event-branding/${scopePath}/${field}/${assetKey}`,
  );
}

export const EVENT_PUBLIC_URL_SCHEME = "evt-public://";

const EVENT_BRANDING_STORAGE_PATH_PATTERN =
  /^events\/branding\/[a-z0-9_-]+\/(?:logoUrl|sponsorLogoUrl|keyVisualUrl)\/[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9]+)?$/;

export function buildEventBrandingUrl(storagePath: string): string {
  return `${EVENT_PUBLIC_URL_SCHEME}${storagePath}`;
}

export function buildConfirmedEventBrandingStoragePath(
  eventIdPublic: EventIdPublic,
  field: EventBrandingField,
  assetKey: string,
): string {
  // eslint-disable-next-line planta-v2/brand-unwrap-only-at-boundaries -- storage path serialization
  const eventKey = EventIdPublic.unwrap(eventIdPublic);
  return `events/branding/${eventKey}/${field}/${assetKey}`;
}

export function resolveEventBrandingStoragePath(url: string): string | null {
  if (!url.startsWith(EVENT_PUBLIC_URL_SCHEME)) {
    return null;
  }
  const storagePath = url.slice(EVENT_PUBLIC_URL_SCHEME.length);
  return EVENT_BRANDING_STORAGE_PATH_PATTERN.test(storagePath)
    ? storagePath
    : null;
}
